/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import urlJoin from 'proper-url-join';
import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import bg500 from '@ibm-watson/shared/assets/images/bg500.svg';
import { wxoUrl } from '../../util/config';

const messages = defineMessages({
  errorDescription: {
    id: 'components.Errors.errorDescription',
    defaultMessage: 'Hmm, it appears there was an internal error.',
    description: 'Error description'
  },
  links: {
    id: 'components.Errors.linksDescription',
    defaultMessage: 'Your admin might be able to help.',
    description: 'Helpful links section'
  },
  home: {
    id: 'components.Errors.links.assistantLogin',
    defaultMessage: 'Return to home page',
    description: 'Link to home page'
  },
  trace: {
    id: 'components.Errors.object.errorTrace',
    defaultMessage: 'Trace: {trace}',
    description: 'Stack trace of error'
  },
  errorMessage: {
    id: 'components.Errors.object.errorMessage',
    defaultMessage: 'Error: {errorMessage}',
    description: 'Error message'
  }
});

interface InternalErrorProps {
  trace?: string;
  errorMessage?: string;
}

const InternalError: React.FC<InternalErrorProps> = ({
  errorMessage,
  trace
}: InternalErrorProps) => {
  const homeUrl = wxoUrl ?? urlJoin(document.baseURI, '/');

  return (
    <div className="ide-http-errors internal-error">
      <div className="bx--grid bx--grid--full-width">
        <div className="bx--row">
          <div className="ide-http-errors-image-container bx--offset-md-3 bx--col-md-4 bx--offset-md-1 bx--offset-max-6 bx--col-max-6 bx--offset-max-4">
            <img alt="Error" className="ide-http-errors-image" src={bg500} />
          </div>
        </div>
        <div className="bx--row">
          <div className="ide-http-errors-title bx--offset-md-3 bx--col-md-4 bx--offset-md-1 bx--offset-max-6 bx--col-max-6 bx--offset-max-4" />
        </div>
        <div className="bx--row">
          <div className="ide-http-errors-description bx--offset-md-3 bx--col-md-4 bx--offset-md-1 bx--offset-max-6 bx--col-max-6 bx--offset-max-4">
            <FormattedMessage {...messages.errorDescription} />
          </div>
        </div>
        <div className="bx--row">
          {errorMessage && (
            <div className="ide-http-errors__error-data bx--offset-md-3 bx--col-md-4 bx--offset-md-1 bx--offset-max-6 bx--col-max-6 bx--offset-max-4">
              <FormattedMessage
                {...messages.errorMessage}
                values={{ errorMessage }}
              />
            </div>
          )}
          {trace && (
            <div className="ide-http-errors-trace bx--offset-md-3 bx--col-md-4 bx--offset-md-1 bx--offset-max-6 bx--col-max-6 bx--offset-max-4">
              <FormattedMessage {...messages.trace} values={{ trace }} />
            </div>
          )}
        </div>
        <div className="bx--row">
          <div className="ide-http-errors-details bx--offset-md-3 bx--col-md-4 bx--offset-md-1 bx--offset-max-6 bx--col-max-6 bx--offset-max-4">
            <FormattedMessage {...messages.links} />
          </div>
        </div>
        <div className="bx--row">
          <div className="ide-http-errors-links bx--offset-md-3 bx--col-md-4 bx--offset-md-1 bx--offset-max-6 bx--col-max-6 bx--offset-max-4">
            <ul>
              <li className="ide-http-errors-links--link">
                <a href={homeUrl} className="bx--link">
                  <FormattedMessage {...messages.home} />
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

InternalError.defaultProps = {
  errorMessage: undefined,
  trace: undefined
};

export default InternalError;
